import React, { useCallback, useEffect, useRef, useState } from "react";
import styles from "../ResourcesPlanner.module.scss";
import { DAY_WIDTHS, canvasWidth, dayToX } from "../engine/timelineGeometry";
import { LOADING_MORE_HEIGHT } from "../constants";
import AllocationActionMenu from "./AllocationActionMenu";
import useTimelineInteractions from "../hooks/useTimelineInteractions";
import { useData, usePlannerEngine, useRowModel, useView } from "../plannerContext";
import CoverageShimmer from "./CoverageShimmer";
import TimelineHeader from "./TimelineHeader";
import TimelineRows from "./TimelineRows";

function TodayLine() {
  const { timeline, today } = usePlannerEngine();
  const viewType = useView((state) => state.viewType);
  const dayWidth = DAY_WIDTHS[viewType];

  return <div className={styles.todayLine} style={{ left: dayToX(timeline, today, dayWidth) + dayWidth / 2 }} />;
}

/** The menu of a bar: quick view and edit. */
function BarMenu({ menu, onClose }) {
  const { dataStore, actions } = usePlannerEngine();
  const menuRef = useRef(null);

  useEffect(() => {
    const onPointerDown = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) onClose();
    };
    document.addEventListener("mousedown", onPointerDown);
    window.addEventListener("scroll", onClose, true);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      window.removeEventListener("scroll", onClose, true);
    };
  }, [onClose]);

  const quickView = () => {
    onClose();

    const { data } = dataStore.getState();
    const allocation = data.allocs[menu.resourceId]?.byTask[menu.taskId];
    if (!allocation) return;

    actions.openFloatingCard(
      { id: allocation.taskId, name: allocation.name, refId: allocation.refId, status: allocation.status, priority: allocation.priority },
      data.scopes[allocation.scopeId],
      data.projects[allocation.projectId],
      {
        startDate: allocation.startDate,
        endDate: allocation.endDate,
        hoursPerDay: allocation.hoursPerDay,
        totalHours: allocation.totalHours,
        isIncludeWeekends: allocation.includeWeekends,
      },
      { x: menu.left, y: menu.top }
    );
  };

  const edit = () => {
    onClose();
    actions.openEditTask(menu.taskId);
  };

  return <AllocationActionMenu position={{ top: menu.top, left: menu.left }} onQuickView={quickView} onEditAllocation={edit} onClose={onClose} menuRef={menuRef} />;
}

/**
 * The timeline: a calendar header, a body that is as wide as the whole span of days and holds only the rows and bars
 * that are on screen, and the overlays that go with them (today, the highlight while dragging, the hours label, the
 * stretches still loading).
 */
export default function PlannerTimeline() {
  const { timeline } = usePlannerEngine();
  const model = useRowModel();
  const viewType = useView((state) => state.viewType);
  const isLoadingMore = useData((state) => state.isLoadingMore);
  const animating = useView((state) => Boolean(state.enteringPrefix));

  const bodyRef = useRef(null);
  const selectionRef = useRef(null);
  const pillRef = useRef(null);
  const [menu, setMenu] = useState(null);
  const closeMenu = useCallback(() => setMenu(null), []);

  const handlers = useTimelineInteractions({ bodyRef, selectionRef, pillRef, onMenu: setMenu });

  const dayWidth = DAY_WIDTHS[viewType];
  const width = canvasWidth(timeline, dayWidth);
  const height = model.totalHeight + (isLoadingMore ? LOADING_MORE_HEIGHT : 0);

  return (
    <div className={`${styles.canvas} ${animating ? styles.animateRows : ""}`} style={{ width }}>
      <TimelineHeader />

      <div ref={bodyRef} className={styles.body} style={{ "--dw": `${dayWidth}px`, width, height, minHeight: "100%" }} {...handlers}>
        <TodayLine />
        <CoverageShimmer />
        <div ref={selectionRef} className={`${styles.selectionHighlight} ${styles.selection}`} />
        <div ref={pillRef} className={`${styles.resourceAllocationPill} ${styles.hoverPill}`}>
          <span className={`${styles.allocationPill} ${styles.allocationPreviewPill} ${styles.pillNormal}`} />
        </div>

        <TimelineRows />

        {isLoadingMore && (
          <div className={styles.skeletonStrip} style={{ top: model.totalHeight }}>
            <div className={styles.skeletonTimelineBar} style={{ width }}>
              <div className={styles.skeletonBar} style={{ left: "5%", width: "25%" }} />
            </div>
            <div className={styles.skeletonTimelineBar} style={{ width }}>
              <div className={styles.skeletonBar} style={{ left: "15%", width: "35%" }} />
            </div>
          </div>
        )}
      </div>

      {menu && <BarMenu menu={menu} onClose={closeMenu} />}
    </div>
  );
}
